import {
  cameraProfileEquals,
  normalizeCameraProfile,
} from '@/etc/cameraProfile'

function normalizeDuration(raw) {
  if (raw === '' || raw === null || typeof raw === 'undefined') return null
  const n = Number(raw)
  return Number.isFinite(n) && n >= 0 ? n : null
}

const normalizeCameraProfilePresetId = (value) => {
  const id = `${value || ''}`.trim()
  return id || null
}

export const MAX_PROMPT_SECTION_HEADLINE_CHARS = 48

// Card headline: display-only, never part of the generation payload.
export function normalizePromptSectionHeadline(value) {
  return `${typeof value === 'string' ? value : ''}`
    .replace(/@/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, MAX_PROMPT_SECTION_HEADLINE_CHARS)
}

export function createEmptyNodePromptSection(id = 1) {
  return {
    id,
    headline: '',
    text: '',
    duration: null,
    cameraProfile: null,
    cameraProfilePresetId: null,
    cameraProfileSuppressed: false,
  }
}

export function normalizeNodePromptSections(sections) {
  const list = Array.isArray(sections) ? sections : []
  const next = list
    .map((section, idx) => {
      const id = section?.id
      const hasId = typeof id === 'string' || typeof id === 'number'
      return {
        id: hasId ? id : idx + 1,
        headline: normalizePromptSectionHeadline(section?.headline),
        text: typeof section?.text === 'string' ? section.text : '',
        duration: normalizeDuration(section?.duration),
        cameraProfile: normalizeCameraProfile(section?.cameraProfile),
        cameraProfilePresetId: normalizeCameraProfilePresetId(
          section?.cameraProfilePresetId,
        ),
        cameraProfileSuppressed:
          section?.cameraProfileSuppressed === true,
      }
    })
    .filter(Boolean)

  return next.length ? next : [createEmptyNodePromptSection()]
}

export function buildNodePromptSectionTimeline(
  sections,
  maxTotalDuration = Number.POSITIVE_INFINITY,
) {
  const list = normalizeNodePromptSections(sections)
  const rawMax = Number(maxTotalDuration)
  const max =
    Number.isFinite(rawMax) && rawMax >= 0
      ? rawMax
      : Number.POSITIVE_INFINITY
  let cursor = 0

  return list.map((section, index) => {
    const requestedDuration = normalizeDuration(section.duration) || 0
    const remaining = Math.max(0, max - cursor)
    const duration = Math.min(requestedDuration, remaining)
    const start = cursor
    const end = start + duration
    cursor = end
    return {
      section,
      index,
      start,
      end,
      duration,
    }
  })
}

export function nodePromptSectionTotalDuration(
  sections,
  maxTotalDuration = Number.POSITIVE_INFINITY,
) {
  const timeline = buildNodePromptSectionTimeline(
    sections,
    maxTotalDuration,
  )
  return timeline.length ? timeline[timeline.length - 1].end : 0
}

export function formatNodePromptTimestamp(rawSeconds) {
  const value = Math.max(0, Math.floor(Number(rawSeconds) || 0))
  const minutes = Math.floor(value / 60)
  const seconds = value % 60
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
}

export function buildPseudoMultiPromptText(
  sections,
  composeSectionText = (section) => `${section?.text || ''}`.trim(),
  maxTotalDuration = Number.POSITIVE_INFINITY,
) {
  const composedSections = normalizeNodePromptSections(sections)
    .map((section) => ({
      ...section,
      text: composeSectionText(section),
    }))
    .filter((section) => `${section.text || ''}`.trim())
  if (!composedSections.length) return ''
  const timeline = buildNodePromptSectionTimeline(
    composedSections,
    maxTotalDuration,
  )
  const shots = timeline
    .map(({ section, index, start, end, duration }) => {
      const text = `${section.text || ''}`.trim()
      const range =
        duration > 0
          ? `(${formatNodePromptTimestamp(start)}–${formatNodePromptTimestamp(end)}) `
          : ''
      return `${range}Shot ${index + 1}:\n${text}`
    })

  if (!shots.length) return ''
  const totalDuration = timeline.length
    ? timeline[timeline.length - 1].end
    : 0
  const header =
    totalDuration > 0
      ? `Sequence duration: ${totalDuration} seconds`
      : 'Sequence'
  return [header, ...shots].join('\n\n')
}

export function createEmptyNodePromptDraft() {
  return {
    text: '',
    cameraProfile: null,
    cameraProfilePresetId: null,
    cameraProfileSuppressed: false,
    sections: [createEmptyNodePromptSection()],
    selectedSectionId: null,
  }
}

export function normalizeNodePromptDraft(draft) {
  const next = draft && typeof draft === 'object' ? draft : {}
  const sections = normalizeNodePromptSections(next.sections)
  const selectedSection = sections.find(
    (section) =>
      next.selectedSectionId != null &&
      `${section.id}` === `${next.selectedSectionId}`,
  )
  return {
    text: typeof next.text === 'string' ? next.text : '',
    cameraProfile: normalizeCameraProfile(next.cameraProfile),
    cameraProfilePresetId: normalizeCameraProfilePresetId(
      next.cameraProfilePresetId,
    ),
    cameraProfileSuppressed: next.cameraProfileSuppressed === true,
    sections,
    selectedSectionId: selectedSection?.id ?? null,
  }
}

export function nodePromptDraftEquals(a, b) {
  const left = normalizeNodePromptDraft(a)
  const right = normalizeNodePromptDraft(b)

  if (left.text !== right.text) return false
  if (!cameraProfileEquals(left.cameraProfile, right.cameraProfile)) return false
  if (left.cameraProfilePresetId !== right.cameraProfilePresetId) return false
  if (left.cameraProfileSuppressed !== right.cameraProfileSuppressed) {
    return false
  }
  if (`${left.selectedSectionId ?? ''}` !== `${right.selectedSectionId ?? ''}`) {
    return false
  }
  if (left.sections.length !== right.sections.length) return false

  for (let i = 0; i < left.sections.length; i += 1) {
    const l = left.sections[i]
    const r = right.sections[i]
    if (l.id !== r.id) return false
    if (l.headline !== r.headline) return false
    if (l.text !== r.text) return false
    if (l.duration !== r.duration) return false
    if (!cameraProfileEquals(l.cameraProfile, r.cameraProfile)) return false
    if (l.cameraProfilePresetId !== r.cameraProfilePresetId) return false
    if (l.cameraProfileSuppressed !== r.cameraProfileSuppressed) return false
  }

  return true
}

export function resolvePromptDraftSelectedSection(
  draft,
  maxTotalDuration = Number.POSITIVE_INFINITY,
) {
  const normalized = normalizeNodePromptDraft(draft)
  if (normalized.selectedSectionId == null) return null
  const timeline = buildNodePromptSectionTimeline(
    normalized.sections,
    maxTotalDuration,
  )
  return (
    timeline.find(
      ({ section }) =>
        `${section.id}` === `${normalized.selectedSectionId}`,
    ) || null
  )
}

export function isDefaultEmptyNodePromptDraft(draft) {
  return nodePromptDraftEquals(draft, createEmptyNodePromptDraft())
}

export function hasEmptyNodePromptSections(draft) {
  const normalized = normalizeNodePromptDraft(draft)
  return normalized.sections.some(
    (section) => !`${section?.text || ''}`.trim(),
  )
}

export function shouldDefaultToNodePromptSections(draft) {
  const normalized = normalizeNodePromptDraft(draft)
  const hasTimedSection = normalized.sections.some(
    (section) => Number(section?.duration) > 0,
  )
  return (
    (normalized.sections.length > 1 || hasTimedSection) &&
    !`${normalized.text || ''}`.trim()
  )
}

export function promptDraftFromDerivedAssetMeta(meta) {
  if (!meta?.derivedClip || typeof meta.derivedClip !== 'object') return null
  const candidate =
    meta.promptDraft || meta.derivedClip?.clipPlan?.promptDraft || null
  return candidate && typeof candidate === 'object'
    ? normalizeNodePromptDraft(candidate)
    : null
}

export function seedNodePromptDraftFromAssetMeta(currentDraft, meta) {
  const incoming = promptDraftFromDerivedAssetMeta(meta)
  if (!incoming || !isDefaultEmptyNodePromptDraft(currentDraft)) {
    return {
      seeded: false,
      promptDraft: normalizeNodePromptDraft(currentDraft),
    }
  }
  return {
    seeded: true,
    promptDraft: incoming,
  }
}
